import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { Link, Spinner, useToast } from '@toqio/toqio-web-components';
import { useBridge } from '../../../bridge/useBridge';
import { useFlowReady } from '../../../bridge/useFlowReady';
import { EmptyState, PageContainer, PageHeader, PageHeaderInfo } from '../../../shared/components';
import { AggregateCounters } from '../components/AggregateCounters';
import { DateRangeDropdown } from '../components/DateRangeDropdown';
import { FilterModal } from '../components/FilterModal';
import { PaymentList } from '../components/PaymentList';
import { CloseIcon, FilterIcon } from '../components/icons';
import { STATUS_TAG_LABELS } from '../status';
import type { PaymentListState } from '../usePaymentList';
import type { DateRangePreset } from '../types';

const FLOW_ID = 'pay-by-bank-backoffice';

/**
 * List screen (PRD §2/§3): filterable, date-grouped payment list with aggregate
 * counters. Bypasses `StepWizard` (independent entry points — LESSONS); emits
 * `step:change` on mount, signals readiness once the first load settles, and on
 * the host `back:request` leaves the embed (`cancel`, PRD O-8 default).
 */
// Horizontal gutter comes from PageContainer (.lum-web-container); only the
// vertical padding lives here.
const Page = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px 0;
  // The DS info Tooltip parks its (hidden) content node off to the right until
  // it's first opened, extending the document width and flashing a horizontal
  // scrollbar on narrow viewports. Clip the horizontal axis; the tooltip opens
  // upward (position="top"), so it stays fully visible. Vertical is untouched.
  overflow-x: clip;
`;

/** "Filter" trigger: the DS Link (theme link colour) + the active-filter dot. */
const FilterTrigger = styled.div`
  position: relative;
  align-self: flex-start;
  margin-top: 24px;
`;

/** Link content: sliders glyph + label, inheriting the DS Link colour. */
const FilterInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

/** Red dot shown on "Filtrar" while any filter is active (live behaviour). */
const Dot = styled.span`
  position: absolute;
  top: -2px;
  right: -10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f44336;
`;

/** Removable active-filter tags below "Filtrar" (live behaviour). */
const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  align-self: flex-start;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #477390;
  color: #fff;
  font:
    400 14px/20px Inter,
    sans-serif;
`;

const TagClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: #fff;
  cursor: pointer;
`;

const ListArea = styled.div`
  margin-top: 16px;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 56px 24px;
`;

export const PaymentListStep: React.FC<{ list: PaymentListState }> = ({ list }) => {
  const bridge = useBridge();
  const navigate = useNavigate();
  const toast = useToast();

  // Sibling-relative navigation from this leaf route (`…/list`) — the splat
  // gotcha in `forms_host_wizard`: navigating from the flow root resolves the
  // relative path against the wrong base.
  const onOpen = (revenueId: string) =>
    navigate(`../detail/${encodeURIComponent(revenueId)}`, { relative: 'path' });
  const { signalReady } = useFlowReady();
  const locale = bridge.locale ?? 'en';

  const { phase, data, filters, applyFilters, loadMore, reload } = list;
  const [filterOpen, setFilterOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    bridge.emit({
      type: 'step:change',
      flowId: FLOW_ID,
      stepId: 'list',
      stepIndex: 0,
      totalSteps: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === 'loading') return;
    signalReady();
  }, [phase, signalReady]);

  useEffect(
    () => bridge.onBackRequest(() => bridge.emit({ type: 'cancel', flowId: FLOW_ID })),
    [bridge],
  );

  const onDateRangeChange = (
    preset: DateRangePreset,
    custom?: { fromDate?: string; toDate?: string },
  ) =>
    applyFilters({ dateRangePreset: preset, fromDate: custom?.fromDate, toDate: custom?.toDate });

  const onLoadMore = async () => {
    setLoadingMore(true);
    try {
      await loadMore();
    } catch {
      toast.error('pbbbo-load-more-error', t`We couldn't load more payments. Please try again.`);
    } finally {
      setLoadingMore(false);
    }
  };

  const rows = data?.data ?? [];
  const currency = rows[0]?.invoiceCurrency ?? 'EUR';

  // Active-filter tags (search / destination / status) shown below "Filtrar".
  // The date-range preset is the dropdown, not a tag.
  const activeTags = [
    filters.externalClientId && {
      key: 'search',
      label: filters.externalClientId,
      remove: () => applyFilters({ externalClientId: '' }),
    },
    filters.clientId && {
      key: 'destination',
      label: filters.clientId,
      remove: () => applyFilters({ clientId: '' }),
    },
    filters.status && {
      key: 'status',
      label: STATUS_TAG_LABELS[filters.status] ?? filters.status,
      remove: () => applyFilters({ status: '' }),
    },
  ].filter(Boolean) as { key: string; label: string; remove: () => void }[];

  return (
    <PageContainer>
      <Page>
        <PageHeader
          title={t`Payments initiated`}
          showDivider={false}
          info={
            <PageHeaderInfo>
              <DateRangeDropdown
                value={filters.dateRangePreset}
                customFrom={filters.fromDate}
                customTo={filters.toDate}
                onChange={onDateRangeChange}
              />
            </PageHeaderInfo>
          }
        />

        <AggregateCounters
          total={data?.total ?? 0}
          totalAmount={data?.totalAmount ?? 0}
          currency={currency}
          locale={locale}
        />

        <FilterTrigger>
          <Link onClick={() => setFilterOpen(true)}>
            <FilterInner>
              <FilterIcon />
              <Trans>Filter</Trans>
            </FilterInner>
          </Link>
          {activeTags.length > 0 && <Dot />}
        </FilterTrigger>

        {activeTags.length > 0 && (
          <Tags>
            {activeTags.map((tag) => (
              <Tag key={tag.key}>
                {tag.label}
                <TagClose type="button" aria-label={t`Remove filter`} onClick={tag.remove}>
                  <CloseIcon />
                </TagClose>
              </Tag>
            ))}
          </Tags>
        )}

        <ListArea>
          {phase === 'loading' && (
            <Centered>
              <Spinner />
            </Centered>
          )}

          {phase === 'error' && (
            <EmptyState
              variant="error"
              description={t`We couldn't load the payments. Please try again.`}
              action={{ label: t`Try again`, onClick: reload }}
            />
          )}

          {phase === 'ready' && rows.length === 0 && (
            <EmptyState
              title={t`No results found`}
              description={t`Try adjusting your filters to find what you are looking for.`}
            />
          )}

          {phase === 'ready' && rows.length > 0 && (
            <PaymentList
              rows={rows}
              locale={locale}
              hasNext={data?.hasNext ?? false}
              loadingMore={loadingMore}
              onOpen={onOpen}
              onLoadMore={onLoadMore}
            />
          )}
        </ListArea>
      </Page>

      <FilterModal
        visible={filterOpen}
        initial={{
          externalClientId: filters.externalClientId,
          clientId: filters.clientId,
          status: filters.status,
        }}
        onApply={(values) => {
          applyFilters(values);
          setFilterOpen(false);
        }}
        onClear={() => {
          applyFilters({ externalClientId: '', clientId: '', status: '' });
          setFilterOpen(false);
        }}
        onClose={() => setFilterOpen(false)}
      />
    </PageContainer>
  );
};
